import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { allWorks } from "@/mocks/works";
import { WORK_TYPE_LABEL } from "@/types/catalog";

const slides = allWorks.filter((work) => work.featured).slice(0, 5);

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next: number) => {
    setIndex(((next % slides.length) + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const current = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[420px] w-full md:h-[600px]">
        {slides.map((slide, slideIndex) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              slideIndex === index ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.banner}
              alt={`${slide.title}剧照`}
              title={`${slide.title} - 爱妻艺影视推荐`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-900/55 to-primary-900/20"></div>
          </div>
        ))}

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col justify-center px-4 md:px-6">
            <div key={current.id} className="max-w-xl animate-fade-up">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500/90 px-3 py-1 text-xs font-medium text-background-50">
                <i className="ri-fire-line"></i>
                今日热播 · {WORK_TYPE_LABEL[current.type]}
              </span>
              <h1 className="mt-4 text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                {current.title}
              </h1>
              <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  {current.rating.toFixed(1)}
                </span>
                <span>{current.year}</span>
                <span>{current.region}</span>
                <span>{current.genres.join(" / ")}</span>
              </p>
              <p className="mt-4 line-clamp-3 text-sm leading-relaxed text-background-100 md:text-base">
                {current.description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.type}/${current.id}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  查看详情
                </Link>
                <Link
                  to={`/category/${current.type}`}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200/60 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  更多同类作品
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:left-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-foreground-950/40 text-background-50 transition-colors hover:bg-foreground-950/70 md:right-6 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((slide, slideIndex) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`切换到第${slideIndex + 1}张`}
              onClick={() => go(slideIndex)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                slideIndex === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}